declare const __GOOGLE_TAG_MANAGER_ID__: string | undefined;
declare const __GOOGLE_ANALYTICS_MEASUREMENT_ID__: string | undefined;
declare const __YANDEX_METRICA_COUNTER_ID__: string | undefined;

type GoogleTagCommand = [command: string, ...args: unknown[]];

type YandexMetricaFunction = {
  (...args: unknown[]): void;
  a?: unknown[][];
  l?: number;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GoogleTagCommand) => void;
    ym?: YandexMetricaFunction;
  }
}

const GOOGLE_SCRIPT_ID = 'motionpitch-google-analytics';
const YANDEX_SCRIPT_ID = 'motionpitch-yandex-metrica';
const GOOGLE_TAG_MANAGER_ID = normalizeGoogleTagManagerId(__GOOGLE_TAG_MANAGER_ID__);
const GOOGLE_MEASUREMENT_ID = normalizeGoogleMeasurementId(__GOOGLE_ANALYTICS_MEASUREMENT_ID__);
const YANDEX_COUNTER_ID = normalizeYandexCounterId(__YANDEX_METRICA_COUNTER_ID__);

let initialized = false;
let navigationBound = false;
let pageViewTimer: number | null = null;
let lastTrackedUrl = '';
let previousTrackedUrl = document.referrer || '';

export function initializeAnalytics(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  if (!GOOGLE_TAG_MANAGER_ID && !GOOGLE_MEASUREMENT_ID && YANDEX_COUNTER_ID === null) {
    return;
  }

  if (GOOGLE_MEASUREMENT_ID) {
    initializeGoogleAnalytics(GOOGLE_MEASUREMENT_ID);
  }
  if (YANDEX_COUNTER_ID !== null) {
    initializeYandexMetrica(YANDEX_COUNTER_ID);
  }

  bindSpaNavigationTracking();
  schedulePageView();
}

export function scheduleAnalyticsPageView(): void {
  if (!initialized || (!GOOGLE_TAG_MANAGER_ID && !GOOGLE_MEASUREMENT_ID && YANDEX_COUNTER_ID === null)) {
    return;
  }
  schedulePageView();
}

function initializeGoogleAnalytics(measurementId: string): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(..._args: GoogleTagCommand): void {
    // gtag.js expects the native Arguments object used by Google's standard snippet.
    window.dataLayer!.push(arguments);
  };

  appendExternalScript(
    GOOGLE_SCRIPT_ID,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  );

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false
  });
}

function initializeYandexMetrica(counterId: number): void {
  if (!window.ym) {
    const ym: YandexMetricaFunction = (...args: unknown[]) => {
      (ym.a ||= []).push(args);
    };
    ym.l = Date.now();
    window.ym = ym;
  }

  appendExternalScript(YANDEX_SCRIPT_ID, `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`);
  window.ym(counterId, 'init', {
    ssr: true,
    defer: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: window.location.href,
    trackLinks: true,
    accurateTrackBounce: true
  });
}

function bindSpaNavigationTracking(): void {
  if (navigationBound) {
    return;
  }
  navigationBound = true;

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = (...args: Parameters<History['pushState']>): void => {
    originalPushState(...args);
    schedulePageView();
  };
  window.history.replaceState = (...args: Parameters<History['replaceState']>): void => {
    originalReplaceState(...args);
    schedulePageView();
  };

  window.addEventListener('popstate', schedulePageView);
  window.addEventListener('hashchange', schedulePageView);
}

function schedulePageView(): void {
  if (pageViewTimer !== null) {
    window.clearTimeout(pageViewTimer);
  }
  pageViewTimer = window.setTimeout(() => {
    pageViewTimer = null;
    trackCurrentPageView();
  }, 0);
}

function trackCurrentPageView(): void {
  const pageUrl = window.location.href;
  if (pageUrl === lastTrackedUrl) {
    return;
  }

  const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const pageTitle = document.title;
  const pageReferer = lastTrackedUrl || previousTrackedUrl;

  if (GOOGLE_TAG_MANAGER_ID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'motionpitch_page_view',
      page_title: pageTitle,
      page_location: pageUrl,
      page_path: pagePath,
      page_referrer: pageReferer
    });
  }

  if (GOOGLE_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_location: pageUrl,
      page_path: pagePath
    });
  }

  if (YANDEX_COUNTER_ID !== null && window.ym) {
    window.ym(YANDEX_COUNTER_ID, 'hit', pageUrl, {
      title: pageTitle,
      referer: pageReferer
    });
  }

  previousTrackedUrl = pageReferer;
  lastTrackedUrl = pageUrl;
}

function appendExternalScript(id: string, src: string): void {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

function normalizeGoogleMeasurementId(value: string | undefined): string {
  const normalized = String(value || '').trim().toUpperCase();
  return /^G-[A-Z0-9]+$/.test(normalized) ? normalized : '';
}

function normalizeGoogleTagManagerId(value: string | undefined): string {
  const normalized = String(value || '').trim().toUpperCase();
  return /^GTM-[A-Z0-9]+$/.test(normalized) ? normalized : '';
}

function normalizeYandexCounterId(value: string | undefined): number | null {
  const normalized = String(value || '').trim();
  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  const counterId = Number(normalized);
  return Number.isSafeInteger(counterId) && counterId > 0 ? counterId : null;
}
